import { useState, useEffect, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { RentalContract } from "shared/types/rental-contracts";
import { fetchContractsCreatedThisYear } from "../api/contracts-this-year";

export const useContractsNearIndexation = () => {
  const { session } = useSession();
  const [contracts, setContracts] = useState<RentalContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/near-indexation`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      setContracts(data);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch contracts near indexation:", error);
      setError("Failed to load contracts near indexation");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchContracts();
    }
  }, [session, fetchContracts]);

  return { contracts, isLoading, error, refetch: fetchContracts };
};

export const useContractsThisYear = () => {
  const { session } = useSession();
  const [contracts, setContracts] = useState<RentalContract[]>([]);
  const [count, setCount] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [contractDetails, setContractDetails] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    if (!session) return;
    try {
      setIsLoading(true);
      const data = await fetchContractsCreatedThisYear(session);
      setContracts(data.contracts);
      setCount(data.count);
      setTotalValue(data.totalValue);
      setContractDetails(data.contractDetails);
      setError(null);
    } catch (error) {
      console.error("Error fetching contracts created this year:", error);
      setError("Failed to load contracts created this year");
    } finally {
      setIsLoading(false);
    }
    // Depend on the stable session id, not the session object reference,
    // to avoid re-fetching on every Clerk re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return {
    contracts,
    count,
    totalValue,
    contractDetails,
    isLoading,
    error,
    refetch: fetchContracts
  };
};

export const useActiveContractsCount = () => {
  const { session } = useSession();
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/metrics/active-contracts-count`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      setCount(data);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch active contracts count:", error);
      setError("Failed to load active contracts count");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchCount();
    }
  }, [session, fetchCount]);

  return { count, isLoading, error, refetch: fetchCount };
};
